<template>
    <BaseDialog v-bind="$attrs" v-model:open="open" title="Work IQ Settings">
        <div class="flex flex-col gap-8 justify-start items-start py-6 md:py-10 px-4 md:px-10 md:min-w-100">
            <div class="w-full">
                <div class="flex flex-row justify-between items-center w-full">
                    <div class="flex flex-row gap-2.5 items-center">
                        <i class="bi bi-microsoft text-[#0078D4]" aria-hidden="true"></i>
                        <h3 class="title-subsection">Work IQ Settings</h3>
                    </div>
                    <BaseButton theme="tertiary" class="p-2!" @click="closeDialog()">
                        <i class="bi bi-x text-3xl text-[#939292]" aria-hidden="true"></i>
                    </BaseButton>
                </div>
            </div>
            <div class="flex flex-col gap-2.5 w-full">
                <div class="body-small">Connection</div>
                <div class="border-2 rounded-xl flex flex-col">
                    <div class="flex flex-row justify-between items-center gap-2 p-2.5">
                        <div class="flex flex-col gap-1 font-normal body-small overflow-x-hidden">
                            <div class="text-[#262626]">Microsoft 365 connected</div>
                            <div class="text-valence-grey-750 text-ellipsis whitespace-nowrap overflow-hidden">{{ userDetails?.email }}</div>
                        </div>
                        <BaseButton v-if="!confirming" theme="tertiary" class="font-medium shrink-0" @click="confirming = true"> Disconnect </BaseButton>
                    </div>
                    <InlineConfirm
                        v-model:open="confirming"
                        prompt="Are you sure you want to disconnect Work IQ?"
                        confirm-label="Yes, disconnect"
                        :is-confirming="isDisconnecting"
                        @confirm="removeWorkIqIntegration" />
                </div>
            </div>
            <div class="flex flex-col gap-2.5 w-full">
                <div class="body-small">Coaching</div>
                <div class="border-2 rounded-xl flex flex-col gap-2 p-2.5">
                    <AccountSettingToggle
                        id="workiq-all-sources"
                        bold
                        label="Let Nadia use my chats, emails, and calendar"
                        :model-value="allSourcesEnabled"
                        @update:model-value="setAllSources"></AccountSettingToggle>
                    <p class="body-small text-valence-grey-750">
                        By default Nadia only reads your Teams meeting transcripts. Turn this on to also let her use your Teams chats, emails, and calendar.
                    </p>
                    <p v-if="allSourcesError" class="body-small text-red-600">Couldn't save that just now. Try again.</p>
                </div>
                <div class="border-2 rounded-xl flex flex-col gap-2 p-2.5">
                    <AccountSettingToggle
                        id="workiq-auto-context"
                        bold
                        label="Let Nadia use my Work IQ data automatically"
                        :model-value="autoContextEnabled"
                        @update:model-value="setAutoContext"></AccountSettingToggle>
                    <!-- Names the sources this actually covers. With the scope toggle off the promise
                         is transcripts-only, and the previous copy claimed chats, email and calendar
                         regardless of scope. -->
                    <p class="body-small text-valence-grey-750">{{ autoContextDescription }}</p>
                    <p v-if="autoContextError" class="body-small text-red-600">Couldn't save that just now. Try again.</p>
                </div>
            </div>
        </div>
    </BaseDialog>
</template>

<script setup lang="ts">
import { router } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import InlineConfirm from "~vue/components/InlineConfirm.vue";
import AccountSettingToggle from "~vue/UserProfile/AccountSettingToggle.vue";
import { logUserInteraction } from "~vue/utils/logUtils";
import type { Ref } from "vue";
import { computed, inject, ref, watch } from "vue";

defineOptions({ inheritAttrs: false });

const props = defineProps<{
    disconnectUrl: string;
    autoContext?: boolean;
    allSources?: boolean;
}>();

const open = defineModel<boolean>("open");

const userDetails = inject<Ref<UserDetails>>("user_details");
const confirming = ref(false);

const autoContextEnabled = ref<boolean>(props.autoContext ?? false);
const autoContextPayload = ref<{ key: string; value: boolean } | null>(null);
const { execute: executeAutoContextUpdate, error: autoContextError } = useFetch("/api/accounts/user/settings", { immediate: false }).post(autoContextPayload).json();

const allSourcesEnabled = ref<boolean>(props.allSources ?? false);
const allSourcesPayload = ref<{ key: string; value: boolean } | null>(null);
const { execute: executeAllSourcesUpdate, error: allSourcesError } = useFetch("/api/accounts/user/settings", { immediate: false }).post(allSourcesPayload).json();

const setAllSources = async (value: boolean) => {
    const previous = allSourcesEnabled.value;
    allSourcesEnabled.value = value;
    allSourcesPayload.value = { key: "workiq_all_sources", value };
    await executeAllSourcesUpdate();
    if (allSourcesError.value) {
        allSourcesEnabled.value = previous;
        return;
    }
    logUserInteraction(value ? "workiq_all_sources_enabled" : "workiq_all_sources_disabled");
};

const autoContextDescription = computed(() =>
    allSourcesEnabled.value
        ? "Nadia will read your Teams meeting transcripts, chats, emails, and calendar whenever they're relevant to what you're discussing, without asking your permission each time. You can turn this off at any time."
        : "Nadia will read your Teams meeting transcripts whenever they're relevant to what you're discussing, without asking your permission each time. You can turn this off at any time.",
);

const setAutoContext = async (value: boolean) => {
    const previous = autoContextEnabled.value;
    autoContextEnabled.value = value;
    autoContextPayload.value = { key: "workiq_auto_context", value };
    await executeAutoContextUpdate();
    if (autoContextError.value) {
        autoContextEnabled.value = previous;
        return;
    }
    logUserInteraction(value ? "workiq_auto_context_enabled" : "workiq_auto_context_disabled");
};

// Component stays mounted across open/close, so reset the confirm prompt when the dialog closes.
watch(open, (isOpen) => {
    if (!isOpen) {
        confirming.value = false;
    }
});

// Same reason: a server round trip (e.g. the reload after disconnect) refreshes the prop
// without remounting, so the local toggle has to follow it.
watch(
    () => props.autoContext,
    (value) => {
        autoContextEnabled.value = value ?? false;
    },
);

watch(
    () => props.allSources,
    (value) => {
        allSourcesEnabled.value = value ?? false;
    },
);

const closeDialog = () => {
    open.value = false;
};

const { execute: executeDisconnect, isFetching: isDisconnecting, onFetchResponse } = useFetch(() => props.disconnectUrl, { immediate: false }).post();

const removeWorkIqIntegration = async () => {
    await executeDisconnect();
    logUserInteraction("workiq_integration_disconnected");
};

onFetchResponse(() => {
    confirming.value = false;
    router.reload();
});
</script>

<style scoped></style>
